import type { ClientCommand, CommandType, PayloadOf } from '@ecobox/schema';
import { isWaitingState, type LedgerEntry } from './ledger';

/**
 * Optimistic overlay (02 §2.2). Projections are written by the server, so a pending command changes
 * nothing in the local cache. Screens read `applyOverlay(projection, pending commands)`.
 *
 * Reducers must be the same functions the server uses; from Phase 1 they come from
 * `packages/domain`. A reducer must be idempotent per command id: the projection may already
 * contain the effect of a command that is still listed as waiting.
 */
export type Reducer<TState, TPayload> = (
  state: TState,
  payload: TPayload,
  command: ClientCommand,
) => TState;

export type Reducers<TState> = {
  [T in CommandType]?: Reducer<TState, PayloadOf<T>>;
};

export interface Overlay<TState> {
  /** Does this overlay have a reducer for the command type? */
  handles(type: string): boolean;
  applyOverlay(base: TState, pending: readonly ClientCommand[]): TState;
}

export function createOverlay<TState>(config: { reducers: Reducers<TState> }): Overlay<TState> {
  const table = config.reducers as Record<string, Reducer<TState, unknown> | undefined>;
  return {
    handles: (type) => table[type] !== undefined,
    applyOverlay(base, pending) {
      let state = base;
      for (const command of pending) {
        const reducer = table[command.type];
        if (!reducer) continue;
        try {
          state = reducer(state, command.payload, command);
        } catch (err) {
          console.error(`Overlay reducer for ${command.type} failed; skipping ${command.id}.`, err);
        }
      }
      return state;
    },
  };
}

/** The commands the overlay should still apply: everything the server has not finished with. */
export function pendingCommands(entries: readonly LedgerEntry[]): ClientCommand[] {
  return entries.filter((e) => isWaitingState(e.state)).map((e) => e.command);
}

// ---- Phase 0 example: devEcho ----------------------------------------------

export interface EchoItem {
  /** The command id; the server writes `devEchoes/{commandId}`. */
  id: string;
  message: string;
  occurredAt: string;
  /** True while it exists only on this device. */
  pending: boolean;
}

export interface EchoListState {
  echoes: readonly EchoItem[];
}

export const echoOverlay: Overlay<EchoListState> = createOverlay<EchoListState>({
  reducers: {
    devEcho: (state, payload, command) => {
      if (state.echoes.some((e) => e.id === command.id)) return state;
      const item: EchoItem = {
        id: command.id,
        message: payload.message,
        occurredAt: command.occurredAt,
        pending: true,
      };
      return { echoes: [item, ...state.echoes] };
    },
  },
});
